/**
 * Low-level bridge to the WebView2 host (the ResWare desktop client).
 *
 * When the AI Assistant runs inside the ResWare desktop client it is hosted in a
 * WebView2 control, which exposes `window.chrome.webview` for host <-> web messaging.
 * In a normal browser (dev server, standalone) `window.chrome.webview` is undefined and
 * every function here is a safe no-op.
 */

interface WebView2HostMessageEvent {
  data: unknown;
}

interface WebView2Host {
  postMessage(message: unknown): void;
  addEventListener(
    type: 'message',
    listener: (event: WebView2HostMessageEvent) => void,
  ): void;
  removeEventListener(
    type: 'message',
    listener: (event: WebView2HostMessageEvent) => void,
  ): void;
}

declare global {
  interface Window {
    chrome?: {
      webview?: WebView2Host;
    };
  }
}

function getHost(): WebView2Host | undefined {
  return typeof window !== 'undefined' ? window.chrome?.webview : undefined;
}

/** True when running inside the ResWare desktop WebView2 host. */
export function isWebView2Host(): boolean {
  return getHost() !== undefined;
}

/** Post a message to the desktop host. No-op outside WebView2. */
export function postToHost(message: unknown): void {
  getHost()?.postMessage(message);
}

/**
 * Subscribe to messages from the desktop host. Returns an unsubscribe function.
 * No-op (returns a no-op unsubscribe) outside WebView2.
 */
export function onHostMessage(handler: (data: unknown) => void): () => void {
  const host = getHost();
  if (!host) return () => {};
  const listener = (event: WebView2HostMessageEvent) => handler(event.data);
  host.addEventListener('message', listener);
  return () => host.removeEventListener('message', listener);
}
